/**
 * ╔════════════════════════════════════════════════════════════════════════╗
 * ║ Menu.tsx — a casca da prancheta                                        ║
 * ╚════════════════════════════════════════════════════════════════════════╝
 *
 * Lateral de 15rem que recolhe para 3.5rem em 60rem. Quem decide o recolhimento
 * é o **CSS**, não um `if` aqui: a largura da coluna é do `grid` da `.casca`,
 * que responde ao mesmo ponto de quebra. Duplicar a decisão em JavaScript é
 * como as duas saem de sincronia numa janela redimensionada.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ Rótulo recolhido é RECORTADO, nunca `display: none`.                     │
 * │                                                                          │
 * │ Ele é o nome acessível do link. `display: none` tira o texto da árvore   │
 * │ de acessibilidade, e o link vira "botão sem nome" para quem navega com   │
 * │ leitor de tela — em toda janela menor que 60rem, que é onde mais gente   │
 * │ está. `gestao.css` recorta com `clip-path`, que esconde do olho e        │
 * │ preserva o nome. `testes/gestao.test.tsx` prova as duas metades: que o   │
 * │ nome acessível continua lá, e que nenhuma regra do CSS apaga o rótulo.   │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * O papel decide o que a lateral OFERECE. Não é controle de acesso: a política
 * de `0003_rls.sql` recusa de novo o que a interface já não mostrou. Esconder
 * "Pessoas" de um `operador` existe para a lateral fazer sentido, não para
 * proteger a tabela.
 */

import { useSessao } from '../../ganchos/useSessao';
import { enderecoDaRota, irPara, type Rota } from '../../rotas';
import { podeGerirPessoas, podeVerAuditoria } from '../../servicos/gestao';
import type { Papel } from '../../tipos';

import { Icone, type ChaveDeIcone } from './icones';

export type ItemDoMenu = {
  readonly nome: string;
  readonly icone: ChaveDeIcone;
  /** `null` quando o item aparece mas ainda não leva a lugar nenhum. */
  readonly href: string | null;
  readonly atual: boolean;
};

export type SecaoDoMenu = {
  /** `null` na primeira: seção de topo não precisa de rótulo. */
  readonly titulo: string | null;
  readonly itens: readonly ItemDoMenu[];
};

/** O formulário aberto agora, quando a rota traz um. */
export function formularioDaRota(rota: Rota): string | null {
  switch (rota.nome) {
    case 'construtor':
    case 'respostas':
    case 'resposta':
    case 'auditoria':
      return rota.formularioId;
    default:
      return null;
  }
}

/**
 * As seções, dado onde estamos e quem somos. Pura de propósito: é o que
 * permite provar em teste que um `operador` não recebe "Pessoas" sem montar
 * sessão, banco nem navegador.
 */
export function montarSecoes(
  rota: Rota,
  papel: Papel | null,
  tituloDoFormulario: string | null,
): SecaoDoMenu[] {
  const formularioId = formularioDaRota(rota);

  const principal: ItemDoMenu[] = [
    {
      nome: 'Formulários',
      icone: 'formularios',
      href: enderecoDaRota({ nome: 'fila' }),
      atual: rota.nome === 'fila',
    },
  ];

  if (podeGerirPessoas(papel)) {
    principal.push({
      nome: 'Pessoas',
      icone: 'pessoas',
      href: enderecoDaRota({ nome: 'pessoas' }),
      atual: rota.nome === 'pessoas',
    });
  }

  const secoes: SecaoDoMenu[] = [{ titulo: null, itens: principal }];

  if (formularioId) {
    const doFormulario: ItemDoMenu[] = [
      {
        nome: 'Construtor',
        icone: 'construtor',
        href: enderecoDaRota({ nome: 'construtor', formularioId }),
        atual: rota.nome === 'construtor',
      },
      {
        nome: 'Respostas',
        icone: 'respostas',
        href: enderecoDaRota({ nome: 'respostas', formularioId }),
        // O detalhe de uma resposta é a fila por dentro: manter "Respostas"
        // marcado ali é o que impede a lateral de parecer que a pessoa saiu do
        // lugar onde está.
        atual: rota.nome === 'respostas' || rota.nome === 'resposta',
      },
    ];

    if (podeVerAuditoria(papel)) {
      doFormulario.push({
        nome: 'Auditoria',
        icone: 'auditoria',
        href: enderecoDaRota({ nome: 'auditoria', formularioId }),
        atual: rota.nome === 'auditoria',
      });
    }

    // O título é o do formulário quando ele já carregou. Enquanto não, a
    // palavra genérica — e não o uuid, que não diz nada a ninguém.
    secoes.push({ titulo: tituloDoFormulario ?? 'Formulário aberto', itens: doFormulario });
  }

  return secoes;
}

export const ROTULO_DE_PAPEL: Readonly<Record<Papel, string>> = {
  proprietario: 'Proprietária',
  administrador: 'Administração',
  operador: 'Operação',
  leitor: 'Leitura',
};

export type PropsDoMenu = {
  readonly secoes: readonly SecaoDoMenu[];
  readonly email: string | null;
  readonly papel: Papel | null;
  readonly aoSair: () => void;
};

/** A tela pura: recebe tudo por props e não busca nada. */
export function Menu({ secoes, email, papel, aoSair }: PropsDoMenu) {
  return (
    <div className="menu">
      <a className="menu__marca" href={enderecoDaRota({ nome: 'fila' })}>
        {/* O losango é o nó do grafo visto de cima: a marca da prancheta. */}
        <svg
          className="menu__icone"
          viewBox="0 0 16 16"
          width="16"
          height="16"
          aria-hidden="true"
          focusable="false"
        >
          <rect
            x="3"
            y="3"
            width="10"
            height="10"
            rx="1.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          />
          <path d="M8 6.25 9.75 8 8 9.75 6.25 8Z" fill="currentColor" />
        </svg>
        <span className="menu__rotulo menu__rotulo--marca">RSVP Orquestrado</span>
      </a>

      {secoes.map((secao, i) => (
        <div className="menu__secao" key={secao.titulo ?? `principal-${i}`}>
          {secao.titulo ? (
            <h2 className="menu__titulo">
              {/* Recolhido, o título da seção some do olho mas fica de rótulo
                  do grupo — a lista continua sendo "as telas deste formulário". */}
              <span className="menu__rotulo">{secao.titulo}</span>
            </h2>
          ) : null}
          <ul className="menu__lista">
            {secao.itens.map((item) => (
              <li key={item.nome}>
                {item.href ? (
                  <a
                    className="menu__item"
                    href={item.href}
                    aria-current={item.atual ? 'page' : undefined}
                  >
                    <Icone chave={item.icone} />
                    <span className="menu__rotulo">{item.nome}</span>
                  </a>
                ) : (
                  <span className="menu__item menu__item--inerte" aria-disabled="true">
                    <Icone chave={item.icone} />
                    <span className="menu__rotulo">{item.nome}</span>
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}

      <div className="menu__conta">
        {email ? (
          <span className="menu__rotulo menu__conta__email" title={email}>
            {email}
          </span>
        ) : null}
        {papel ? <span className="menu__rotulo etiqueta">{ROTULO_DE_PAPEL[papel]}</span> : null}
        <button type="button" className="menu__item menu__item--botao" onClick={aoSair}>
          <Icone chave="sair" />
          <span className="menu__rotulo">Sair</span>
        </button>
      </div>
    </div>
  );
}

/**
 * A versão que busca. É esta que `App.tsx` monta — uma prop só.
 *
 * O título do formulário aberto não é buscado aqui: quem já o tem é a tela, e
 * uma segunda ida ao banco só para escrever o nome na lateral pagaria uma
 * requisição por navegação. Enquanto ele não chega, a seção se chama
 * "Formulário aberto".
 */
export function MenuDaPrancheta({
  rota,
  tituloDoFormulario = null,
}: {
  rota: Rota;
  tituloDoFormulario?: string | null;
}) {
  const sessao = useSessao();
  const secoes = montarSecoes(rota, sessao.papel, tituloDoFormulario);

  return (
    <Menu
      secoes={secoes}
      email={sessao.identidade?.email ?? null}
      papel={sessao.papel}
      aoSair={() => {
        void sessao.sair().then(() => irPara({ nome: 'entrar' }));
      }}
    />
  );
}
